import { useState } from 'react';

const CONTRACT = {
  oltukai: {
    name: 'Ol Tukai Lodge — Amboseli', cur: 'USD',
    seasons: [
      { id: 'LOW', label: 'Low', from: '04-01', to: '06-30', base: { BB: 180, HB: 210, FB: 240 } },
      { id: 'HIGH', label: 'High', from: '07-01', to: '10-31', base: { BB: 295, HB: 335, FB: 375 } },
      { id: 'SHLDR', label: 'Shoulder', from: '11-01', to: '12-19', base: { BB: 215, HB: 250, FB: 285 } },
      { id: 'PEAK', label: 'Peak', from: '12-20', to: '01-03', base: { BB: 340, HB: 385, FB: 430 } },
      { id: 'SHLDR2', label: 'Shoulder', from: '01-04', to: '03-31', base: { BB: 215, HB: 250, FB: 285 } }
    ],
    child: [{ max: 2, pct: 0, note: 'infant — free of charge' }, { max: 11, pct: 50, note: 'child 3–11 sharing — 50% of adult rate' }, { max: 15, pct: 75, note: 'teen 12–15 sharing — 75% of adult rate' }],
    festive: { from: '12-24', to: '12-26', amount: 95, note: 'mandatory Christmas gala dinner, per adult' }
  },
  serena: {
    name: 'Mara Serena Safari Lodge', cur: 'USD',
    seasons: [
      { id: 'LOW', label: 'Low', from: '04-01', to: '06-15', base: { BB: 205, HB: 245, FB: 280 } },
      { id: 'HIGH', label: 'Migration', from: '06-16', to: '10-15', base: { BB: 390, HB: 440, FB: 495 } },
      { id: 'SHLDR', label: 'Shoulder', from: '10-16', to: '12-19', base: { BB: 250, HB: 290, FB: 330 } },
      { id: 'PEAK', label: 'Peak', from: '12-20', to: '01-05', base: { BB: 430, HB: 480, FB: 540 } },
      { id: 'SHLDR2', label: 'Shoulder', from: '01-06', to: '03-31', base: { BB: 250, HB: 290, FB: 330 } }
    ],
    child: [{ max: 1, pct: 0, note: 'infant — free of charge' }, { max: 12, pct: 50, note: 'child 2–12 sharing — 50% of adult rate' }, { max: 17, pct: 100, note: '16+ charged as adult' }],
    festive: { from: '12-31', to: '12-31', amount: 120, note: "mandatory New Year's Eve gala, per adult" }
  },
  kilaguni: {
    name: 'Kilaguni Serena — Tsavo West', cur: 'USD',
    seasons: [
      { id: 'LOW', label: 'Low', from: '04-01', to: '06-30', base: { BB: 150, HB: 180, FB: 205 } },
      { id: 'HIGH', label: 'High', from: '07-01', to: '10-31', base: { BB: 235, HB: 270, FB: 305 } },
      { id: 'SHLDR', label: 'Shoulder', from: '11-01', to: '12-19', base: { BB: 175, HB: 205, FB: 235 } },
      { id: 'PEAK', label: 'Peak', from: '12-20', to: '01-03', base: { BB: 275, HB: 310, FB: 350 } },
      { id: 'SHLDR2', label: 'Shoulder', from: '01-04', to: '03-31', base: { BB: 175, HB: 205, FB: 235 } }
    ],
    child: [{ max: 2, pct: 0, note: 'infant — free of charge' }, { max: 11, pct: 40, note: 'child 3–11 sharing — 40% of adult rate' }, { max: 15, pct: 70, note: 'teen 12–15 sharing — 70% of adult rate' }],
    festive: null
  }
};

const PLAN_LABEL = { BB: 'bed & breakfast', HB: 'half board', FB: 'full board' };
function md(d) { return d.slice(5); }
function inBand(m, from, to) { return from <= to ? (m >= from && m <= to) : (m >= from || m <= to); }

export default function useRateResolver() {
  // Expo announcement visibility state
  const EXPO_ENDS = new Date('2026-10-09T00:00:00+03:00');
  const showExpo = Date.now() <= EXPO_ENDS.getTime();
  const [expoStripVisible, setExpoStripVisible] = useState(true);

  // Rate calculator form state
  const [prop, setProp] = useState('oltukai');
  const [date, setDate] = useState('2026-08-14');
  const [plan, setPlan] = useState('FB');
  const [ad, setAd] = useState(2);
  const [ch, setCh] = useState(1);
  const [ages, setAges] = useState([9]);

  // Stepper handlers
  const handleAdChange = (delta) => setAd((prev) => Math.min(100, Math.max(1, prev + delta)));
  const handleChChange = (delta) => {
    setCh((prevCh) => {
      const nextCh = Math.min(100, Math.max(0, prevCh + delta));
      setAges((prevAges) => {
        const nextAges = prevAges.slice(0, nextCh);
        while (nextAges.length < nextCh) nextAges.push(9);
        return nextAges;
      });
      return nextCh;
    });
  };
  const handleAgeChange = (index, val) => {
    setAges((prev) => {
      const next = [...prev];
      next[index] = Number(val);
      return next;
    });
  };

  // Live calculations
  const c = CONTRACT[prop];
  const m = md(date || '2026-08-14');
  const season = c.seasons.find((s) => inBand(m, s.from, s.to)) || c.seasons[0];
  const adultRate = season.base[plan];

  let total = 0;
  const adSub = adultRate * ad;
  total += adSub;

  const childRows = ages.map((age, i) => {
    const band = c.child.find((b) => age <= b.max) || { pct: 100, note: 'charged as adult' };
    const amt = Math.round((adultRate * band.pct) / 100);
    total += amt;
    return { id: i, label: `child ${i + 1} (${age} yr)`, note: band.note, amt };
  });

  let festiveRow = null;
  if (c.festive && inBand(m, c.festive.from, c.festive.to)) {
    const fAmt = c.festive.amount * ad;
    total += fAmt;
    festiveRow = { note: c.festive.note, amt: fAmt };
  }

  return {
    CONTRACT, PLAN_LABEL, showExpo, expoStripVisible, setExpoStripVisible,
    prop, setProp, date, setDate, plan, setPlan, ad, ch, ages,
    handleAdChange, handleChChange, handleAgeChange,
    c, season, adultRate, adSub, childRows, festiveRow, total
  };
}